import {User, UserService} from "./app/services/user.service";
import {CanActivateFn, CanMatchFn, GuardResult, Router} from "@angular/router";
import {inject} from "@angular/core";
import {map, take} from "rxjs";
import {isInSignupFlow} from "./app/model/user";
import {CompletionStatus, isCompletionStatus, userMatchesCompletionStatus} from "./app/model/completion-status";

// Virtual statuses: mandatory-flow users without preferences are still in the signup flow; the
// completion statuses are in completion-status.ts.
export type UserStatusOrSignupFlow = User['status'] | 'signup-flow' | CompletionStatus;

const userMatchesStatus = (user: User, status: UserStatusOrSignupFlow): boolean => {
  if (status === 'signup-flow') {
    return isInSignupFlow(user);
  }
  return isCompletionStatus(status) ? userMatchesCompletionStatus(user, status) : user.status === status;
};

export const gotoLoginIfNotAuthenticated: (userStateToRoute: Partial<Record<User['status'], string[]>>) => CanActivateFn = (userStateToRoute) => (route, state) => {
  const userService = inject(UserService);
  const router = inject(Router);

  return userService.user$.pipe(
    take(1),
    map(user => {
      if (user.status === 'unauthorized') {
        return router.createUrlTree(['/login'], {queryParams: {redirectUrl: state.url}});
      }

      const stateRoute = userStateToRoute[user.status];
      if (stateRoute) {
        return router.createUrlTree(stateRoute);
      }

      return true;
    })
  );
}

export const gotoHomepageIfLoggedInGuard: CanActivateFn = () => {
  const userService = inject(UserService);
  const router = inject(Router);

  return userService.user$.pipe(
    take(1),
    map(user => {
      if (user.status === 'unauthorized') {
        return true;
      }

      return router.createUrlTree(['/']);
    })
  );
}

export const postSignupFlowGuard: CanActivateFn = (route, state) => {
  const userService = inject(UserService);
  const router = inject(Router);

  return userService.user$.pipe(
    take(1),
    map(user => {
      if (user.status === 'unauthorized') {
        return router.createUrlTree(['/login'], {queryParams: {redirectUrl: state.url}});
      }

      if (isInSignupFlow(user)) {
        return router.createUrlTree(['/']);
      }

      return true;
    })
  );
}


export const redirectOnUserState = (redirectFn: (router: Router, user: User) => GuardResult): CanActivateFn => () => {
  const userService = inject(UserService);
  const router = inject(Router);

  return userService.user$.pipe(
    take(1),
    map(user => {
      return redirectFn(router, user);
    })
  );
}

export const redirectIfUserNotInStates = (statuses: UserStatusOrSignupFlow[], route = ['/']): CanActivateFn => redirectOnUserState((router, user) => {
    if (!statuses.some(status => userMatchesStatus(user, status))) {
      return router.createUrlTree(route);
    }

    return true;
  });

export const isUserInStates = (statuses: UserStatusOrSignupFlow[]): CanMatchFn => () => {
  const userService = inject(UserService);

  return userService.user$.pipe(
    take(1),
    map(user => statuses.some(status => userMatchesStatus(user, status)))
  );
}
